import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { authFetch } from '@/lib/auth-fetch';
import { getApiBaseUrl } from '@/lib/data-service';

/**
 * Data Mode Context
 * 
 * Global context for managing Demo/Live/Tour data mode across the application.
 * 
 * - Demo Mode: Uses mock/demo data (no backend connectivity)
 * - Live Mode: Uses real backend data (requires backend to be connected)
 * - Tour Mode: Uses tour-specific mock data during product tours
 * 
 * The selected mode is persisted to the backend (UserPreferences) at a user level
 * so that it follows the user across browsers/devices. localStorage is used as a
 * fast cache and offline fallback.
 */

export type DataMode = 'demo' | 'live' | 'tour';

interface DataModeContextValue {
  mode: DataMode;
  setMode: (mode: DataMode) => void;
  isLiveMode: boolean;
  isDemoMode: boolean;
  isTourMode: boolean;
  isModeLocked: boolean;
}

const DataModeContext = createContext<DataModeContextValue | undefined>(undefined);

const STORAGE_KEY = 'evo-data-mode';

// In production builds (import.meta.env.PROD = true) on non-localhost hosts,
// default to 'live' so reads and writes hit the real API. In development builds
// (e.g. Evo Builder sandbox, local dev) default to 'demo' so the app works
// without a running backend — developers and sandbox users can switch to 'live'
// via the DataModeSelector if they have a backend available.
const DEFAULT_MODE: DataMode =
  typeof window !== 'undefined' &&
  import.meta.env?.PROD &&
  window.location.hostname !== 'localhost' &&
  window.location.hostname !== '127.0.0.1'
    ? 'live'
    : 'demo';

interface DataModeProviderProps {
  children: ReactNode;
}

function isValidDataMode(value: string | null | undefined): value is DataMode {
  return value === 'demo' || value === 'live' || value === 'tour';
}

const LOCKED_KEY = 'evo-data-mode-locked';

export function DataModeProvider({ children }: DataModeProviderProps) {
  const isModeLocked = typeof window !== 'undefined' && localStorage.getItem(LOCKED_KEY) === 'true';
  const lockedMode = isModeLocked
    ? (localStorage.getItem(STORAGE_KEY) as DataMode) || DEFAULT_MODE
    : null;

  const [mode, setModeState] = useState<DataMode>(() => {
    if (lockedMode) return lockedMode;

    // Initialize from localStorage (fast) or default to demo
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isValidDataMode(stored)) {
        return stored;
      }
    }
    return DEFAULT_MODE;
  });

  // On mount, try to load from backend and override localStorage value if available
  // Skip when mode is locked — the entry point has already set the correct mode.
  useEffect(() => {
    if (isModeLocked) return;

    const loadFromBackend = async () => {
      try {
        const backendUrl = getApiBaseUrl();
        const response = await authFetch(`${backendUrl}/UserPreferences/me`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          if (data?.selectedDataMode && isValidDataMode(data.selectedDataMode)) {
            localStorage.setItem(STORAGE_KEY, data.selectedDataMode);
            setModeState(data.selectedDataMode);
          }
        }
      } catch {
        // Backend unavailable – keep localStorage / default value
      }
    };

    loadFromBackend();
  }, []);

  // Persist mode changes to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const saveToBackend = useCallback((newMode: DataMode) => {
    const backendUrl = getApiBaseUrl();
    authFetch(`${backendUrl}/UserPreferences/me/selected-data-mode`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selectedDataMode: newMode }),
    }).catch((err) => {
      console.error('Failed to save selected data mode to backend:', err);
    });
  }, []);

  const setMode = (newMode: DataMode) => {
    if (isModeLocked) return;
    localStorage.setItem(STORAGE_KEY, newMode);
    setModeState(newMode);
    // Persist to backend asynchronously (don't block UI)
    saveToBackend(newMode);
  };

  const value: DataModeContextValue = {
    mode,
    setMode,
    isLiveMode: mode === 'live',
    isDemoMode: mode === 'demo',
    isTourMode: mode === 'tour',
    isModeLocked,
  };

  return (
    <DataModeContext.Provider value={value}>
      {children}
    </DataModeContext.Provider>
  );
}

export function useDataMode() {
  const context = useContext(DataModeContext);
  if (context === undefined) {
    throw new Error('useDataMode must be used within a DataModeProvider');
  }
  return context;
}
